import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, ShieldCheck, Globe2, Gauge, Gavel, ListChecks,
  Mic, FileText, Languages, Lock, Zap, Quote, Check, Play, BrainCircuit,
  AudioLines, CalendarCheck, TrendingUp, Server, BadgeCheck, Menu, X,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import { API_BASE } from "@/lib/api";
import { detectRegion, rememberRegion, formatMoney } from "@/lib/region";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import TalkToSalesDialog from "@/components/TalkToSalesDialog";

/* ───────────────────────── reveal-on-scroll ───────────────────────── */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(22px)",
        transition: `opacity .7s cubic-bezier(.2,.7,.2,1) ${delay}ms, transform .7s cubic-bezier(.2,.7,.2,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ───────────────────────── data ───────────────────────── */
const TRANSCRIPT = [
  { who: "Priya", role: "VP Product", text: "Let's lock the global launch for March 14.", tag: "DECISION" },
  { who: "Karan", role: "Security", text: "I'll send the SOC 2 evidence pack by Friday.", tag: "ACTION" },
  { who: "Mei", role: "APAC", text: "Singapore needs data residency before we sign.", tag: "RISK" },
  { who: "Dev", role: "Platform", text: "Rate limit holds to 10k RPS — verified yesterday.", tag: null },
  { who: "Priya", role: "VP Product", text: "Owner: Karan. Due: next standup.", tag: "OWNER" },
];

const TAG_TONE = {
  DECISION: "tone-powder",
  ACTION: "tone-sage",
  RISK: "tone-blush",
  OWNER: "tone-apricot",
};

const METRICS = [
  { v: "98.4%", l: "transcription accuracy", sub: "validated-segment benchmark" },
  { v: "6.4 h", l: "saved per person / week", sub: "no more minute-taking" },
  { v: "42 s", l: "to full minutes", sub: "after the call ends" },
  { v: "31", l: "languages supported", sub: "incl. 12 Indian languages" },
];

const STEPS = [
  {
    n: "01", icon: Mic, title: "Capture",
    body: "Record in-browser or join a live room. Every participant stream is captured with consent, waveform and pause control — no bots, no plug-ins, no uploads to third parties.",
  },
  {
    n: "02", icon: BrainCircuit, title: "Understand",
    body: "Our Quality Guard validates every segment before the AI reads it — voice-activity detection, language ID, hallucination and loop filters. Garbage never becomes a decision.",
  },
  {
    n: "03", icon: Gavel, title: "Execute",
    body: "Decisions enter an approval register. Action items become owned, dated tasks on a Kanban board with one-click email confirmations and automated reminders.",
  },
];

const FEATURES = [
  {
    icon: AudioLines, title: "Multilingual transcription",
    body: "Sarvam AI for Indic and code-switched speech, Whisper for global coverage. Speaker diarization maps voices to real participant names.",
    chips: ["English", "हिन्दी", "தமிழ்", "తెలుగు", "Español", "Français"],
  },
  {
    icon: ShieldCheck, title: "Transcript Quality Guard",
    body: "A transparent 0–100 quality gate. Repetition loops, hallucinations, foreign-script drift and silence gaps are caught and repaired before intelligence runs.",
    chips: ["VAD coverage", "Auto-repair", "Gap map"],
  },
  {
    icon: Gauge, title: "Meeting Health Score",
    body: "Six weighted components — on-agenda time, agenda coverage, decision density, participation balance, questions answered, time-box discipline — each with a fix-it tip.",
    chips: ["Agenda drift alerts", "Participation entropy"],
  },
  {
    icon: Gavel, title: "Decision Register",
    body: "Every decision gets a lifecycle: Proposed → Pending approval → Approved or Rejected → Implemented, with an immutable approval trail and follow-up tasks.",
    chips: ["Audit trail", "Email sign-off"],
  },
  {
    icon: ListChecks, title: "Execution board",
    body: "Drag-and-drop Kanban, owners, due dates, checklists, watchers and @mentions. Smart reminders chase people so you don't have to.",
    chips: ["Kanban", "Reminders", "Guest share"],
  },
  {
    icon: FileText, title: "Board-ready minutes",
    body: "A branded, sectioned PDF of agenda, executive summary, decisions, actions, risks and open questions — emailed to every attendee minutes after you hang up.",
    chips: ["PDF export", "Auto-email"],
  },
];

const LANGUAGES = [
  "English", "हिन्दी", "தமிழ்", "తెలుగు", "ಕನ್ನಡ", "മലയാളം", "मराठी", "বাংলা",
  "Español", "Français", "Deutsch", "Português", "العربية", "日本語", "中文", "Bahasa",
];

const TESTIMONIALS = [
  {
    quote:
      "We used to lose three decisions a week in Slack threads. MeetIQ360 turned our Monday review into a register our board actually audits.",
    name: "Ananya Rao", role: "COO, fintech · Bengaluru",
    img: "https://images.unsplash.com/photo-1573496130141-209d200cebd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=240",
  },
  {
    quote:
      "Half our standups are Hindi-English code-switched. It is the only tool that transcribed them accurately enough to trust the action items.",
    name: "Rohit Menon", role: "Head of Engineering, logistics",
    img: "https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&q=85&w=240",
  },
  {
    quote:
      "The quality gate is the differentiator. Other tools confidently invented action items. This one tells us when the audio was not good enough.",
    name: "Claire Dubois", role: "VP Operations, EU manufacturing",
    img: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=85&w=240",
  },
];

const COMPLIANCE = [
  { icon: Lock, label: "AES-256 at rest" },
  { icon: ShieldCheck, label: "SOC 2 ready controls" },
  { icon: Server, label: "IN · EU · US · SG regions" },
  { icon: BadgeCheck, label: "GDPR & DPDP aligned" },
  { icon: FileText, label: "GST tax invoicing" },
  { icon: Globe2, label: "99.9% uptime target" },
];

// Marketing copy per plan; prices come live from GET /api/billing/public-catalog.
const PLAN_COPY = {
  launch: {
    name: "Launch", seats: "2 – 10 people",
    tagline: "For focused teams that want their decisions written down.",
    features: ["Unlimited meetings", "Multilingual transcription", "AI minutes & tasks", "Decision register", "Email minutes"],
  },
  accelerate: {
    name: "Accelerate", seats: "11 – 50 people", popular: true,
    tagline: "For scaling orgs running cross-functional execution.",
    features: ["Everything in Launch", "Meeting Health analytics", "Quality Guard reports", "Smart reminders & guest sign-off", "Ask Yanthra AI assistant", "Priority support"],
  },
  command: {
    name: "Command", seats: "51+ people",
    tagline: "For enterprises with audit, residency and SSO needs.",
    features: ["Everything in Accelerate", "Data residency choice", "SSO & SCIM", "Custom retention policy", "Dedicated success manager", "Annual contracting"],
  },
};

const PLAN_ORDER = ["launch", "accelerate", "command"];

const REGION_LABEL = {
  IN: { l: "India · INR", note: "Billed in INR via UPI or card, with a GST tax invoice." },
  INTL: { l: "Global · USD", note: "Billed in USD by card. Local tax applied where required." },
};

const NAV_LINKS = [
  { href: "#platform", label: "Platform" },
  { href: "#how", label: "How it works" },
  { href: "#global", label: "Global" },
  { href: "#assistant", label: "AI assistant" },
  { href: "#pricing", label: "Pricing" },
];

/* ───────────────────────── small pieces ───────────────────────── */
const Eyebrow = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1.5"
  >
    <Sparkles className="w-3 h-3" style={{ color: "hsl(var(--cyan))" }} strokeWidth={2.4} />
    <span className="eyebrow text-[10px]">{children}</span>
  </div>
);

const SectionHead = ({ kicker, title, body, align = "left" }) => (
  <div className={align === "center" ? "max-w-3xl" : "max-w-2xl"}>
    <div className="eyebrow">{kicker}</div>
    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">{title}</h2>
    {body && <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">{body}</p>}
  </div>
);

/* Animated product mock used in the hero. */
function HeroMock() {
  const [quality, setQuality] = useState(71);
  const [idx, setIdx] = useState(2);
  useEffect(() => {
    const a = setInterval(() => setQuality((q) => Math.min(99, Math.max(68, q + (Math.random() * 10 - 4)))), 1400);
    const b = setInterval(() => setIdx((i) => (i + 1) % TRANSCRIPT.length), 2600);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);

  return (
    <div className="relative" data-testid="landing-hero-mock">
      <div className="absolute -inset-6 yl-mesh rounded-[42px] blur-2xl opacity-70" aria-hidden="true" />
      <div className="relative yl-glass yl-beam yl-beam-glow rounded-[30px] shadow-lift overflow-hidden">
        {/* chrome */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/70">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-rose-500 yl-pulse-dot" />
              <span className="absolute inset-0 rounded-full bg-rose-500" />
            </span>
            <span className="eyebrow text-rose-600 dark:text-rose-400 text-[10px]">Live · Global launch review</span>
          </div>
          <div className="flex items-end gap-[3px] h-4">
            {[0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72].map((d, i) => (
              <span
                key={i}
                className="yl-wave-bar block w-[3px] rounded-full"
                style={{ height: "100%", background: "hsl(var(--cyan))", animationDelay: `${d}s` }}
              />
            ))}
          </div>
        </div>

        {/* quality meter */}
        <div className="px-5 pt-4">
          <div className="flex items-center justify-between text-[11px]">
            <span className="eyebrow text-[9px]">Transcript quality</span>
            <span className="font-mono font-semibold" style={{ color: "hsl(var(--ink-success))" }}>
              {Math.round(quality)}%
            </span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${quality}%`,
                background: "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--navy)))",
                transition: "width 1.2s cubic-bezier(.2,.7,.2,1)",
              }}
            />
          </div>
        </div>

        {/* transcript */}
        <div className="px-5 py-4 space-y-2.5">
          {TRANSCRIPT.map((l, i) => (
            <div
              key={l.who + i}
              className="flex items-start gap-3 rounded-2xl px-3 py-2.5 border"
              style={{
                borderColor: i === idx ? "hsl(var(--cyan) / .45)" : "transparent",
                background: i === idx ? "hsl(var(--cyan) / .08)" : "transparent",
                opacity: i > idx ? 0.42 : 1,
                transition: "background-color .5s ease, border-color .5s ease, opacity .5s ease",
              }}
            >
              <span
                className="shrink-0 w-7 h-7 rounded-lg grid place-items-center text-[10px] font-bold text-white"
                style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
              >
                {l.who[0]}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold">{l.who}</span>
                  <span className="font-mono text-[9px] text-muted-foreground uppercase tracking-wider">{l.role}</span>
                </div>
                <p className="text-sm text-foreground/85 leading-snug mt-0.5">{l.text}</p>
              </div>
              {l.tag && (
                <span className={`${TAG_TONE[l.tag]} shrink-0 rounded-full px-2 py-0.5 font-mono text-[9px] font-bold tracking-wider`}>
                  {l.tag}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* extracted */}
        <div className="grid grid-cols-3 border-t border-border/70 divide-x divide-border/70">
          {[
            { v: "3", l: "decisions", icon: Gavel },
            { v: "5", l: "action items", icon: ListChecks },
            { v: "92%", l: "on agenda", icon: Gauge },
          ].map((k) => (
            <div key={k.l} className="px-4 py-4">
              <k.icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.8} />
              <div className="font-heading text-2xl leading-none mt-2">{k.v}</div>
              <div className="eyebrow text-[9px] mt-1 text-muted-foreground">{k.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* floating decision chip */}
      <div
        className="absolute -right-6 lg:-right-12 top-1/3 yl-float hidden sm:block"
        style={{ animationDelay: "1.2s" }}
      >
        <div className="yl-glass rounded-2xl shadow-lift px-4 py-3 w-56">
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-3.5 h-3.5" style={{ color: "hsl(var(--ink-success))" }} strokeWidth={2.2} />
            <span className="eyebrow text-[9px]">Task created</span>
          </div>
          <p className="text-xs font-semibold mt-1.5 leading-snug">Send SOC 2 evidence pack</p>
          <p className="font-mono text-[10px] text-muted-foreground mt-1">Karan · due Fri</p>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── page ───────────────────────── */
export default function Landing() {
  const [region, setRegion] = useState(detectRegion);
  const [autoRegion, setAutoRegion] = useState(!localStorage.getItem("meetiq_region"));
  const [cycle, setCycle] = useState("monthly");
  const [catalog, setCatalog] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesPlan, setSalesPlan] = useState("");
  const openSales = (plan = "") => { setSalesPlan(plan); setSalesOpen(true); };

  // Live prices + server-side (edge geo) region. The browser's own
  // timezone/locale guess is the fallback when the edge can't locate us.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/billing/public-catalog`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setCatalog(d);
        if (!localStorage.getItem("meetiq_region") && d.detected_region) {
          setRegion(d.detected_region);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingPrices(false);
      });
    return () => { cancelled = true; };
  }, []);

  const changeRegion = (r) => {
    setRegion(r);
    setAutoRegion(false);
    rememberRegion(r);
  };

  return (
    <div data-testid="landing-root" className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* ─────────── NAV ─────────── */}
      <header
        data-testid="landing-nav"
        className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-xl"
      >
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 h-16 flex items-center gap-6">
          <Link to="/home" data-testid="landing-brand-link" className="shrink-0">
            <BrandMark size="sm" showByline={false} testid="landing-brand" />
          </Link>
          <nav className="hidden lg:flex items-center gap-7 ml-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`landing-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-semibold text-foreground/70 hover:text-primary transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
            <Link
              to="/login"
              data-testid="landing-signin-btn"
              className="hidden sm:inline-flex items-center h-9 px-4 rounded-full border border-border bg-card text-sm font-semibold text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/login"
              data-testid="landing-cta-nav"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-[hsl(var(--primary-hover))] transition-colors"
            >
              Start free
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
            </Link>
            <button
              type="button"
              data-testid="landing-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden w-9 h-9 rounded-full border border-border grid place-items-center text-foreground/70"
            >
              {menuOpen ? <X className="w-4 h-4" strokeWidth={2} /> : <Menu className="w-4 h-4" strokeWidth={2} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div data-testid="landing-mobile-menu" className="lg:hidden border-t border-border bg-card px-5 py-4 space-y-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                data-testid={`landing-mnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block px-3 py-2.5 rounded-xl text-sm font-semibold text-foreground/80 hover:bg-secondary transition-colors"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <LanguageSwitcher testid="language-switcher-mobile" />
              <ThemeToggle testid="theme-toggle-mobile" />
              <Link
                to="/login"
                data-testid="landing-mobile-signin"
                className="ml-auto inline-flex items-center h-9 px-4 rounded-full border border-border text-sm font-semibold"
              >
                Sign in
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ─────────── HERO ─────────── */}
      <section className="relative">
        <div className="absolute inset-0 yl-grid-lines pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="yl-orb yl-orb-a opacity-40" />
          <div className="yl-orb yl-orb-b opacity-40" />
        </div>
        <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-14 lg:gap-16 items-center">
          <div>
            <Reveal>
              <Eyebrow testid="landing-hero-eyebrow">Global AI meeting intelligence</Eyebrow>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-7">
                Every meeting ends
                <br />
                with <span className="font-display yl-shimmer-text">verified action.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
                MeetIQ360 records, transcribes and <b className="text-foreground font-semibold">validates</b> what was
                actually said in 31 languages — then turns it into approved decisions, owned tasks and board-ready
                minutes before anyone leaves the room.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/login"
                  data-testid="landing-hero-cta"
                  className="group inline-flex items-center gap-2 h-12 px-6 rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-lift hover:bg-[hsl(var(--primary-hover))] transition-colors"
                >
                  Start free — no card needed
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
                </Link>
                <a
                  href="#how"
                  data-testid="landing-hero-secondary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-border bg-card text-sm font-bold text-foreground/85 hover:text-primary hover:border-primary/50 transition-colors"
                >
                  <Play className="w-3.5 h-3.5" strokeWidth={2.4} />
                  See how it works
                </a>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
                {[
                  { icon: Zap, t: "Minutes in 42 seconds" },
                  { icon: Languages, t: "31 languages" },
                  { icon: ShieldCheck, t: "Quality-gated AI" },
                ].map((p) => (
                  <span key={p.t} className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                    <p.icon className="w-3.5 h-3.5" style={{ color: "hsl(var(--cyan))" }} strokeWidth={2.2} />
                    {p.t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={200}>
            <HeroMock />
          </Reveal>
        </div>
      </section>

      {/* ─────────── METRICS BAND ─────────── */}
      <section className="border-y border-border bg-card/50">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-9">
          {METRICS.map((m, i) => (
            <Reveal key={m.l} delay={i * 70}>
              <div data-testid={`landing-metric-${i}`}>
                <div className="font-heading text-3xl sm:text-4xl leading-none">{m.v}</div>
                <div className="text-sm font-semibold mt-2">{m.l}</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">{m.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────── HOW IT WORKS ─────────── */}
      <section id="how" className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <SectionHead
            kicker="The system"
            title="Capture. Understand. Execute."
            body="Most AI note-takers stop at a summary. MeetIQ360 is built around the uncomfortable truth that meetings only matter if something changes afterwards."
          />
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 110}>
              <div
                data-testid={`landing-step-${s.n}`}
                className="yl-surface yl-hover-lift yl-beam h-full p-7 lg:p-8"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 rounded-2xl grid place-items-center"
                    style={{ background: "hsl(var(--cyan) / .12)" }}
                  >
                    <s.icon className="w-5 h-5" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={1.9} />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground/70">{s.n}</span>
                </div>
                <h3 className="font-heading text-xl sm:text-2xl mt-6">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-3">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────── FEATURE BENTO ─────────── */}
      <section id="platform" className="border-y border-border bg-secondary/25">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <SectionHead
              kicker="The platform"
              title="An execution OS, not a note-taker."
              body="Six systems working on the same transcript — each one auditable, each one replacing a spreadsheet you maintain by hand today."
            />
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <div
                  data-testid={`landing-feature-${i}`}
                  className="group yl-surface yl-hover-lift h-full p-7 flex flex-col"
                >
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center"
                    style={{ background: "hsl(var(--navy) / .1)" }}
                  >
                    <f.icon className="w-[19px] h-[19px] text-primary" strokeWidth={1.9} />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl mt-5">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3 flex-1">{f.body}</p>
                  <div className="flex flex-wrap gap-1.5 mt-5">
                    {f.chips.map((c) => (
                      <span
                        key={c}
                        className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground/70"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── QUALITY GUARD SPOTLIGHT ─────────── */}
      <section className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative rounded-[28px] overflow-hidden border border-border shadow-lift">
            <img
              src="https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Executive boardroom with city skyline at dusk"
              data-testid="landing-boardroom-img"
              className="w-full h-[320px] sm:h-[420px] object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(170deg, hsl(var(--navy) / .5), hsl(var(--navy) / .08) 45%, hsl(var(--navy) / .72))" }}
            />
            <div className="absolute inset-x-5 bottom-5 yl-glass rounded-2xl p-5">
              <div className="eyebrow text-[9px]">Quality gate · this meeting</div>
              <div className="mt-3 space-y-2.5">
                {[
                  { l: "Speech coverage", v: 94 },
                  { l: "Language match", v: 100 },
                  { l: "Repaired segments", v: 11 },
                ].map((r) => (
                  <div key={r.l}>
                    <div className="flex items-center justify-between text-[11px] font-semibold">
                      <span>{r.l}</span>
                      <span className="font-mono">{r.v}%</span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${r.v}%`, background: "hsl(var(--cyan))" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <SectionHead
            kicker="Trust layer"
            title="AI that admits when the audio was bad."
            body="Every competitor will happily invent an action item from a garbled line. We validate first — voice-activity detection, per-segment language ID, repetition and hallucination filters, bounded re-transcription — and only validated speech reaches the model."
          />
          <ul className="mt-8 space-y-4">
            {[
              "Per-segment verdicts: ok · repaired · flagged · excluded",
              "Health Score and agenda analysis suppressed when the gate fails",
              "Coverage gap map so you can see exactly what was missed",
              "One-click re-transcription of only the failed ranges",
            ].map((li) => (
              <li key={li} className="flex items-start gap-3">
                <span
                  className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0"
                  style={{ background: "hsl(var(--cyan) / .15)" }}
                >
                  <Check className="w-3 h-3" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={3} />
                </span>
                <span className="text-sm text-foreground/85 leading-relaxed">{li}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* ─────────── AI ASSISTANT ─────────── */}
      <section id="assistant" className="relative border-y border-border overflow-hidden">
        <div className="absolute inset-0 yl-mesh opacity-80" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-[1fr_0.9fr] gap-14 items-center">
          <Reveal>
            <SectionHead
              kicker="Ask Yanthra AI"
              title="A real-time avatar that knows your workspace."
              body="Talk to your meeting history. Our photoreal streaming avatar listens continuously, answers in natural speech, and is hard-scoped to your organisation's own data — never the open internet."
            />
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { icon: Mic, t: "Always-on listening", d: "Continuous voice activity detection — no wake word in live mode." },
                { icon: Lock, t: "Scoped to your data", d: "Declines anything outside your workspace and the product." },
                { icon: Zap, t: "Acts, not just answers", d: "Creates tasks, approves decisions, schedules meetings by voice." },
                { icon: TrendingUp, t: "Confirm before acting", d: "Every mutating command shows a plan and waits for your yes." },
              ].map((c) => (
                <div key={c.t} className="yl-surface p-5">
                  <c.icon className="w-4 h-4 text-primary" strokeWidth={2} />
                  <div className="text-sm font-bold mt-3">{c.t}</div>
                  <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={140}>
            <div className="relative mx-auto w-full max-w-sm" data-testid="landing-assistant-mock">
              <div
                className="rounded-[30px] overflow-hidden border border-border shadow-lift"
                style={{ background: "linear-gradient(180deg, #0a1f4d, #0d327a 55%, #1546a8)" }}
              >
                <div className="h-56 relative grid place-items-center">
                  <div className="absolute inset-0 yl-noise" />
                  <div className="relative w-28 h-28 rounded-full grid place-items-center" style={{ background: "radial-gradient(circle, hsl(var(--cyan) / .5), transparent 70%)" }}>
                    <div className="w-20 h-20 rounded-full yl-float grid place-items-center" style={{ background: "hsl(var(--cyan) / .22)", border: "1px solid hsl(var(--cyan) / .5)" }}>
                      <AudioLines className="w-8 h-8 text-white" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/12 backdrop-blur px-3 py-1.5">
                    <span className="relative inline-flex w-2 h-2">
                      <span className="absolute inset-0 rounded-full bg-emerald-400 yl-pulse-dot" />
                      <span className="absolute inset-0 rounded-full bg-emerald-400" />
                    </span>
                    <span className="font-mono text-[10px] text-white/90 uppercase tracking-wider">Listening</span>
                  </div>
                </div>
                <div className="bg-card p-5 space-y-3">
                  <div className="rounded-2xl bg-secondary/70 px-4 py-3">
                    <div className="eyebrow text-[9px]">You</div>
                    <p className="text-sm mt-1">What did we decide about data residency in Singapore?</p>
                  </div>
                  <div className="rounded-2xl px-4 py-3" style={{ background: "hsl(var(--cyan) / .1)" }}>
                    <div className="eyebrow text-[9px]">Yanthra AI</div>
                    <p className="text-sm mt-1 leading-relaxed">
                      On 12 June the APAC review approved SG residency as a launch blocker. Owner: Mei. It is pending
                      implementation — shall I open a follow-up task?
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────── GLOBAL ─────────── */}
      <section id="global" className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <SectionHead
            kicker="Built for the world"
            title="Your team code-switches. So does our engine."
            body="Real meetings mix languages mid-sentence. MeetIQ360 identifies the language of every segment, picks the right engine, and keeps the English translation aligned line by line."
          />
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-12 relative overflow-hidden rounded-3xl border border-border bg-card py-7">
            <div className="flex w-max yl-marquee-x gap-3 px-3">
              {[...LANGUAGES, ...LANGUAGES].map((l, i) => (
                <span
                  key={l + i}
                  className="shrink-0 rounded-full border border-border bg-secondary/50 px-5 py-2.5 text-sm font-semibold text-foreground/80"
                >
                  {l}
                </span>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24" style={{ background: "linear-gradient(90deg, hsl(var(--card)), transparent)" }} />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24" style={{ background: "linear-gradient(270deg, hsl(var(--card)), transparent)" }} />
          </div>
        </Reveal>

        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {[
            { icon: Globe2, t: "Regional data residency", d: "Choose India, EU, US or Singapore storage. Recordings never leave the region you pick." },
            { icon: FileText, t: "Local tax compliance", d: "GST-compliant invoices with CGST/SGST/IGST splits for India; clean USD invoicing globally." },
            { icon: Languages, t: "Localised interface", d: "The workspace speaks English, Hindi, Tamil, Telugu, Spanish, French and Arabic — including right-to-left." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 90}>
              <div data-testid={`landing-global-${i}`} className="yl-surface yl-hover-lift h-full p-7">
                <c.icon className="w-5 h-5 text-primary" strokeWidth={1.9} />
                <h3 className="font-heading text-lg mt-5">{c.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2.5">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─────────── TESTIMONIALS ─────────── */}
      <section className="border-y border-border bg-secondary/25">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <SectionHead kicker="Proof" title="Teams stopped taking minutes. Nothing got lost." />
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tm, i) => (
              <Reveal key={tm.name} delay={i * 100}>
                <figure data-testid={`landing-testimonial-${i}`} className="yl-surface yl-beam h-full p-7 flex flex-col">
                  <Quote className="w-6 h-6" style={{ color: "hsl(var(--cyan))" }} strokeWidth={1.8} />
                  <blockquote className="text-base leading-relaxed mt-5 flex-1 text-foreground/90">
                    “{tm.quote}”
                  </blockquote>
                  <figcaption className="flex items-center gap-3 mt-7 pt-5 border-t border-border">
                    <img
                      src={tm.img}
                      alt={tm.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-bold truncate">{tm.name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate uppercase tracking-wider">{tm.role}</div>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {COMPLIANCE.map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-2.5 rounded-2xl border border-border bg-card px-4 py-3.5"
                >
                  <c.icon className="w-4 h-4 shrink-0" style={{ color: "hsl(var(--cyan))" }} strokeWidth={2} />
                  <span className="text-[11px] font-semibold leading-tight text-foreground/75">{c.label}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────── PRICING ─────────── */}
      <section id="pricing" className="mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <SectionHead
              kicker="Pricing"
              title="Per seat. Per month. No surprises."
              body={REGION_LABEL[region].note}
            />
          </Reveal>
          <Reveal delay={80}>
            <div className="flex flex-col items-start lg:items-end gap-3">
              <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="landing-region-toggle">
                {["INTL", "IN"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`landing-region-${k.toLowerCase()}`}
                    onClick={() => changeRegion(k)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                      region === k ? "bg-primary text-primary-foreground" : "text-foreground/60 hover:text-foreground"
                    }`}
                  >
                    {REGION_LABEL[k].l}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="landing-cycle-toggle">
                {[
                  { k: "monthly", l: "Monthly" },
                  { k: "annual", l: "Annual · save 20%" },
                ].map((c) => (
                  <button
                    key={c.k}
                    type="button"
                    data-testid={`landing-cycle-${c.k}`}
                    onClick={() => setCycle(c.k)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                      cycle === c.k ? "bg-secondary text-secondary-foreground" : "text-foreground/60 hover:text-foreground"
                    }`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
              {autoRegion && (
                <span
                  data-testid="landing-region-auto"
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground uppercase tracking-wider"
                >
                  <Globe2 className="w-3 h-3" strokeWidth={2} />
                  Detected from your location
                </span>
              )}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {PLAN_ORDER.map((key, i) => {
            const copy = PLAN_COPY[key];
            const plan = (catalog?.plans || []).find((x) => x.plan === key);
            const rate = plan?.prices?.[region]?.[cycle];
            const custom = rate == null;
            const popular = !!copy.popular;
            return (
              <Reveal key={key} delay={i * 100}>
                <div
                  data-testid={`landing-plan-${key}`}
                  className={`relative h-full p-7 lg:p-8 rounded-3xl border flex flex-col ${
                    popular ? "border-transparent shadow-lift text-white" : "yl-surface yl-hover-lift"
                  }`}
                  style={popular ? { background: "linear-gradient(165deg, hsl(var(--navy)), #0a1a3d)" } : {}}
                >
                  {popular && (
                    <span
                      className="absolute top-6 right-6 rounded-full px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-wider"
                      style={{ background: "hsl(var(--gold))", color: "#1a1405" }}
                    >
                      Most chosen
                    </span>
                  )}
                  <div className={`eyebrow ${popular ? "text-white/70" : ""}`}>{copy.seats}</div>
                  <h3 className="font-heading text-2xl mt-3">{copy.name}</h3>
                  <p className={`text-sm mt-2.5 leading-relaxed ${popular ? "text-white/70" : "text-muted-foreground"}`}>
                    {copy.tagline}
                  </p>

                  <div className="mt-7 flex items-end gap-1.5">
                    <span data-testid={`landing-price-${key}`} className="font-heading text-4xl leading-none">
                      {loadingPrices ? "—" : custom ? "Custom" : formatMoney(region, rate)}
                    </span>
                    {!custom && !loadingPrices && (
                      <span className={`text-xs font-semibold pb-1 ${popular ? "text-white/60" : "text-muted-foreground"}`}>
                        / seat / mo
                      </span>
                    )}
                  </div>
                  <div className={`font-mono text-[10px] mt-1.5 ${popular ? "text-white/50" : "text-muted-foreground"}`}>
                    {custom
                      ? "Annual contract · talk to sales"
                      : region === "IN"
                        ? `+ ${Math.round(catalog?.gst_rate ?? 18)}% GST · UPI or card${cycle === "annual" ? " · billed yearly" : ""}`
                        : `Card in USD${cycle === "annual" ? " · billed yearly" : ""}`}
                  </div>

                  <ul className="mt-7 space-y-3 flex-1">
                    {copy.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check
                          className="w-3.5 h-3.5 mt-1 shrink-0"
                          style={{ color: popular ? "hsl(var(--cyan))" : "hsl(var(--ink-success))" }}
                          strokeWidth={3}
                        />
                        <span className={popular ? "text-white/85" : "text-foreground/85"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {custom ? (
                    <button
                      type="button"
                      onClick={() => openSales(copy.name || key)}
                      data-testid={`landing-plan-cta-${key}`}
                      className={`mt-8 inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-bold transition-colors ${
                        popular
                          ? "bg-white text-[hsl(var(--navy))] hover:bg-white/90"
                          : "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"
                      }`}
                    >
                      Talk to sales
                      <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      data-testid={`landing-plan-cta-${key}`}
                      className={`mt-8 inline-flex items-center justify-center gap-2 h-11 rounded-full text-sm font-bold transition-colors ${
                        popular
                          ? "bg-white text-[hsl(var(--navy))] hover:bg-white/90"
                          : "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]"
                      }`}
                    >
                      Start 14-day trial
                      <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} />
                    </Link>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section className="relative overflow-hidden border-t border-border">
        <div className="absolute inset-0" style={{ background: "linear-gradient(140deg, hsl(var(--navy)), #0a1a3d 60%, #061027)" }} />
        <div className="absolute inset-0 yl-noise" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1280px] px-5 sm:px-8 py-20 lg:py-28 text-white">
          <div className="max-w-3xl">
            <div className="eyebrow text-white/60">Start today</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.08]">
              Your next meeting can be the last one
              <br className="hidden sm:block" />
              <span className="font-display" style={{ color: "hsl(var(--cyan))" }}> nobody remembers.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/70 leading-relaxed">
              Create a workspace in under two minutes. Invite your team, run one meeting, and read the minutes before
              you've finished your coffee.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/login"
                data-testid="landing-final-cta"
                className="group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-[hsl(var(--navy))] text-sm font-bold hover:bg-white/90 transition-colors"
              >
                Create your workspace
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.4} />
              </Link>
              <button
                type="button"
                onClick={() => openSales("")}
                data-testid="landing-contact-sales"
                className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-white/25 text-sm font-bold text-white/90 hover:bg-white/10 transition-colors"
              >
                Talk to sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── FOOTER ─────────── */}
      <footer data-testid="landing-footer" className="bg-card border-t border-border">
        <div className="mx-auto max-w-[1280px] px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div>
            <BrandMark size="md" testid="landing-footer-brand" />
            <p className="text-sm text-muted-foreground leading-relaxed mt-5 max-w-xs">
              AI meeting intelligence for teams that are measured on what they shipped, not what they discussed.
            </p>
          </div>
          {[
            { h: "Platform", links: ["Transcription", "Quality Guard", "Decision Register", "Execution board", "Ask Yanthra AI"] },
            { h: "Company", links: ["About Yanthra Labs"],
              hrefs: [
                { l: "Privacy Policy", to: "/legal/privacy" },
                { l: "Terms of Service", to: "/legal/terms" },
                { l: "Data Processing Addendum", to: "/legal/dpa" },
                { l: "Cookies", to: "/legal/cookies" },
              ] },
            { h: "Regions", links: ["India (INR · GST)", "European Union", "United States", "Singapore", "Middle East"] },
          ].map((col) => (
            <div key={col.h}>
              <div className="eyebrow text-[9px]">{col.h}</div>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <span className="text-sm text-muted-foreground">{l}</span>
                  </li>
                ))}
                {(col.hrefs || []).map((h) => (
                  <li key={h.l}>
                    <Link
                      to={h.to}
                      data-testid={`footer-${h.to.split("/").pop()}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {h.l}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-border">
          <div className="mx-auto max-w-[1280px] px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-wider">
              © {new Date().getFullYear()} Yanthra Labs · MeetIQ360 ·{" "}
              <Link to="/legal/privacy" className="hover:text-primary transition-colors">GDPR &amp; DPDP compliant</Link>
            </span>
            <div className="flex items-center gap-2">
              <LanguageSwitcher testid="language-switcher-footer" />
              <ThemeToggle testid="theme-toggle-footer" />
            </div>
          </div>
        </div>
      </footer>

      <TalkToSalesDialog open={salesOpen} onOpenChange={setSalesOpen} plan={salesPlan} />
    </div>
  );
}
